import type { SearchResponse } from '@/types/agent';
import { createLocalId } from './helpers';

/**
 * 安全解析搜索结果 JSON
 */
export function parseSearchResult(content: string): SearchResponse | null {
  try {
    return JSON.parse(content) as SearchResponse;
  } catch {
    return null;
  }
}

/**
 * 标准化搜索结果数据
 *
 * 🔴 优化：后端已统一使用 snake_case，移除驼峰命名兼容逻辑
 */
export function normalizeSearchData(raw: any): SearchResponse {
  return {
    query: raw.query || '',
    results: raw.results || [],
    summary: raw.summary || raw.error || '',
    search_type: raw.search_type || 'general',
    optimized_query: raw.optimized_query,
    crawled_pages: raw.crawled_pages,
    suggestions: raw.suggestions || [],
    saved_images: raw.saved_images,
    vision_summary: raw.vision_summary,
    answer: raw.answer,
    source: raw.source,
    phase: raw.phase,
    confidence: raw.confidence,
    search_logs: raw.search_logs,
  } as SearchResponse;
}

/**
 * 🔴 P7优化：统一的搜索数据提取函数
 *
 * 从后端消息对象（type='search'）中提取标准化的 SearchResponse
 * 供 messageRestore.ts 和其他模块统一使用，避免各模块独立实现导致的数据格式不一致
 *
 * 提取优先级：
 * 1. metadata.skill_result（流式路径的完整 JSON）
 * 2. content（非流式路径完整 JSON）
 * 3. content 仅为 summary 文本时，创建最小化 fallback
 */
export function extractSearchDataFromMessage(m: {
  type?: string;
  content?: string;
  metadata?: Record<string, any>;
}): SearchResponse | null {
  if (m.type !== 'search') return null;

  let searchData: SearchResponse | null = null;

  // 1. metadata.skill_result（流式路径的完整 JSON）
  if (m.metadata?.skill_result) {
    try {
      const skillResult = typeof m.metadata.skill_result === 'string'
        ? JSON.parse(m.metadata.skill_result)
        : m.metadata.skill_result;
      searchData = normalizeSearchData(skillResult);
    } catch { /* 继续尝试其他来源 */ }
  }

  // 2. content（非流式路径，现在 SearchOrchestrator 返回完整 JSON）
  if (!searchData && m.content) {
    try {
      const content = typeof m.content === 'string' ? JSON.parse(m.content) : m.content;
      if (content && (content.results || content.query)) {
        searchData = normalizeSearchData(content);
      }
    } catch {
      // content 不是 JSON，可能是错误文本（如"搜索执行失败，请稍后重试"）
      // 不创建空 results 的 SearchResponse，返回 null，让前端显示错误文本而非空搜索卡片
      return null;
    }
  }

  return searchData;
}

/**
 * 创建标准的搜索消息对象
 */
export function createSearchMessage(
  searchData: SearchResponse,
  options?: { 
    id?: string;
    introText?: string;
    processLogs?: any[];
  }
) {
  return {
    id: options?.id || createLocalId(),
    role: 'model' as const,
    text: searchData.summary || `搜索「${searchData.query || ''}」的结果`,
    type: 'search' as const,
    searchData,
    introText: options?.introText,
    processLogs: options?.processLogs,
  };
}
